import { useTranslation } from "react-i18next";
import CreativeMetricsSection from "./CreativeMetricsSection";
import PublisherMetricsSection from "./PublisherMetricsSection";
import { components } from "../../types/api";
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useDispatch } from "react-redux";
import { setToastMessage } from "../../store/slices/app/appSlice";
import CampaignDetailsCard from "../../components/card/CampaignDetailsCard";
import {
  formatCurrency,
  formatDecimal,
  formatNumber,
  formatPercentage,
  formatPercentageNoMultiply,
} from "../../utils/formatUtils";
import MetricsTimelineChart, {
  type MetricDataPoint,
} from "../../components/card/MetricsTimelineChart";
import { exportMetricsToCSV } from "../../utils/exportUtils";
import { useGetMergedCampaignMetricsQuery } from "../../store/apis/internalCampaignsMergeApi";

import OrbLoader from "../common/OrbLoader";
interface NatAdsMergeCampaignDetailsProps {
  isLoadingMergedCampaign: boolean;
  mergedCampaignId: string | undefined;
  mergedCampaignData:
    | components["schemas"]["MergedCampaignMetrics"]
    | null
    | undefined;
}

const NatAdsMergeCampaignDetails: React.FC<NatAdsMergeCampaignDetailsProps> = ({
  isLoadingMergedCampaign,
  mergedCampaignId,
  mergedCampaignData,
}) => {
  const getChartDataPoints = (
    dailyData: components["schemas"]["DailyMetrics"][] = []
  ): MetricDataPoint[] =>
    dailyData.map((day) => ({
      date: day.date,
      ctr: day.ctr ?? 0,
      cpc: day.cpc ?? 0,
      cpm: day.cpm ?? 0,
      impressions: day.impressions ?? 0,
      clicks: day.clicks ?? 0,
      cost: day.cost ?? 0,
      completeViews: day.completeViews ?? 0,
    }));

  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  // Fetch filtered merged campaign metrics only when a date filter is active
  const {
    data: filteredMergedCampaignData,
    isLoading: isLoadingFilteredMerged,
    refetch: refetchFilteredMerged,
  } = useGetMergedCampaignMetricsQuery(
    {
      mergedCampaignId: mergedCampaignId || "",
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
    },
    {
      skip: !mergedCampaignId || (!startDate && !endDate),
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  // Ensure we refetch whenever the date filters change
  useEffect(() => {
    if (!mergedCampaignId) return;
    // If no filters, we rely on unfiltered data
    if (!startDate && !endDate) return;
    refetchFilteredMerged();
  }, [mergedCampaignId, startDate, endDate, refetchFilteredMerged]);

  // Whether a filter is currently active
  const isFilterActive = Boolean(startDate || endDate);

  const formatDateForInput = useCallback(
    (dateString: string | undefined): string => {
      if (!dateString) return "";
      const date = new Date(dateString);
      return date.toISOString().split("T")[0];
    },
    []
  );

  // Campaign default dates for display (do not trigger fetch)
  const campaignStartDate = formatDateForInput(mergedCampaignData?.startDate);
  const campaignEndDate = formatDateForInput(mergedCampaignData?.endDate);
  const displayStartDate = startDate || campaignStartDate;
  const displayEndDate = endDate || campaignEndDate;

  const effectiveMergedCampaignData =
    isFilterActive && filteredMergedCampaignData
      ? filteredMergedCampaignData
      : mergedCampaignData;
  const effectiveIsLoading = isLoadingMergedCampaign || isLoadingFilteredMerged;

  const handleExportMetrics = useCallback(() => {
    if (!effectiveMergedCampaignData?.metrics?.dailyData) {
      dispatch(
        setToastMessage({
          message: t("common.noDataToExport", {
            defaultValue: "No data to export",
          }),
          severity: "warning",
        })
      );
      return;
    }

    // Transform the data to match the CampaignMetric interface
    const formattedData = effectiveMergedCampaignData?.metrics?.dailyData.map(
      (day) => ({
        date: day.date,
        impressions: day.impressions || 0,
        clicks: day.clicks || 0,
        ctr: day.ctr || 0,
        cpm: day.cpm || 0,
        cpc: day.cpc || 0,
        cost: day.cost || 0,
        cpv: day.cpv || 0,
        vtr: day.vtr || 0,
        dailyInvestment: day.cost || 0, // Use cost as dailyInvestment
        dailyImpressions: day.impressions || 0, // Use impressions as dailyImpressions
        completeViews: day.completeViews || 0,
        dailyCTR: day.ctr || 0, // Use ctr as dailyCTR
        conversions: day.conversions || 0,
        currency: effectiveMergedCampaignData?.currency_enum || "USD",
      })
    );

    exportMetricsToCSV(
      formattedData,
      effectiveMergedCampaignData?.name || "export",
      t,
      dispatch
    );
  }, [effectiveMergedCampaignData, t, dispatch]);

  return (
    <>
      {effectiveIsLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "50vh",
          }}
        >
          <OrbLoader size="large" />
        </Box>
      ) : (
        <>
          {/* Metrics */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              borderRadius: 2,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 2, sm: 0 },
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                {t("campaigns.metrics", { defaultValue: "Metrics" })}
              </Typography>

              {/* Date Filter */}
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  rowGap: 2,
                  flexWrap: "wrap",
                }}
              >
                <TextField
                  label={t("common.startDate", {
                    defaultValue: "Start Date",
                  })}
                  type="date"
                  size="small"
                  value={displayStartDate || ""}
                  onChange={(e) => setStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: campaignStartDate,
                    max: endDate || campaignEndDate,
                  }}
                  sx={{ width: 150 }}
                />

                <TextField
                  label={t("common.endDate", {
                    defaultValue: "End Date",
                  })}
                  type="date"
                  size="small"
                  value={displayEndDate || ""}
                  onChange={(e) => setEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    min: startDate || campaignStartDate,
                    max: campaignEndDate,
                  }}
                  sx={{ width: 150 }}
                  disabled={!displayStartDate}
                />

                <IconButton
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                  size="small"
                  color="primary"
                  title={t("common.clearFilters", {
                    defaultValue: "Clear Filters",
                  })}
                >
                  <ClearIcon />
                </IconButton>

                {/* Export Button */}
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportMetrics}
                  sx={{
                    mt: { xs: 1, sm: 0 },
                    ml: { xs: 0, sm: 1 },
                    height: 36,
                    textTransform: "none",
                    fontWeight: 500,
                  }}
                >
                  {t("campaigns.exportMetrics", {
                    defaultValue: "Export Data",
                  })}
                </Button>
              </Box>
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Metrics cards */}
            {effectiveMergedCampaignData?.metrics?.summary && (
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 3,
                  "& > *": {
                    flex: "1 1 calc(20% - 24px)", // 5 cards per row (24px is the gap * (5-1)/5)
                    minWidth: "180px",
                  },
                }}
              >
                {/* Cost */}
                <CampaignDetailsCard
                  value={formatCurrency(
                    effectiveMergedCampaignData.metrics.summary.totalCost,
                    effectiveMergedCampaignData.currency_enum
                  )}
                  label={t("campaigns.cost", {
                    defaultValue: "Total Cost",
                  })}
                />

                {/* Impressions */}
                <CampaignDetailsCard
                  value={formatNumber(
                    effectiveMergedCampaignData.metrics.summary.totalImpressions
                  )}
                  label={t("campaigns.impressions", {
                    defaultValue: "Impressions",
                  })}
                />

                {/* Clicks */}
                <CampaignDetailsCard
                  value={formatNumber(
                    effectiveMergedCampaignData.metrics.summary.totalClicks
                  )}
                  label={t("campaigns.clicks", {
                    defaultValue: "Clicks",
                  })}
                />

                {/* CPM */}
                <CampaignDetailsCard
                  value={formatCurrency(
                    effectiveMergedCampaignData.metrics.summary.avgCPM,
                    effectiveMergedCampaignData.currency_enum
                  )}
                  label={t("campaigns.cpm", { defaultValue: "CPM" })}
                />

                {/* CPC */}
                <CampaignDetailsCard
                  value={formatCurrency(
                    effectiveMergedCampaignData.metrics.summary.avgCPC,
                    effectiveMergedCampaignData.currency_enum
                  )}
                  label={t("campaigns.cpc", { defaultValue: "CPC" })}
                />

                {/* CTR */}
                <CampaignDetailsCard
                  value={formatPercentage(
                    effectiveMergedCampaignData.metrics.summary.avgCTR
                  )}
                  label={t("campaigns.ctr", { defaultValue: "CTR" })}
                />

                {/* Conversions */}
                <CampaignDetailsCard
                  value={formatNumber(
                    effectiveMergedCampaignData.metrics.summary
                      .totalConversions || 0
                  )}
                  label={t("campaigns.totalConversions", {
                    defaultValue: "Conversions",
                  })}
                />

                {/* Conversion Rate */}
                <CampaignDetailsCard
                  value={formatPercentageNoMultiply(
                    effectiveMergedCampaignData.metrics.summary
                      .conversionRate || 0
                  )}
                  label={t("campaigns.conversionRate", {
                    defaultValue: "Conversion Rate",
                  })}
                />

                {/* CPA */}
                <CampaignDetailsCard
                  value={formatCurrency(
                    effectiveMergedCampaignData.metrics.summary.cpa || 0,
                    effectiveMergedCampaignData.currency_enum
                  )}
                  label={t("campaigns.cpa", {
                    defaultValue: "CPA",
                  })}
                />

                {/*
                 * Reach y frecuencia solo cuando el backend tiene el reach real del
                 * periodo (reachScope EXACT), igual que en DV360Cards. Hoy lo dan
                 * Reddit y DV360; en Outbrain y Taboola no sale.
                 */}
                {effectiveMergedCampaignData.metrics.summary.reachScope ===
                  "EXACT" && (
                  <>
                    <CampaignDetailsCard
                      value={formatNumber(
                        effectiveMergedCampaignData.metrics.summary.uniqueUsers
                      )}
                      label={t("campaigns.uniqueUsers", {
                        defaultValue: "Unique Users",
                      })}
                    />
                    <CampaignDetailsCard
                      value={formatDecimal(
                        effectiveMergedCampaignData.metrics.summary.frequency
                      )}
                      label={t("campaigns.frequency", {
                        defaultValue: "Frequency",
                      })}
                    />
                  </>
                )}

                {/* Vídeo solo si hay visualizaciones: en display serían ceros sin sentido */}
                {effectiveMergedCampaignData.metrics.summary
                  .totalCompleteViews > 0 && (
                  <>
                    <CampaignDetailsCard
                      value={formatNumber(
                        effectiveMergedCampaignData.metrics.summary
                          .totalCompleteViews
                      )}
                      label={t("campaigns.completeViews", {
                        defaultValue: "Complete Views",
                      })}
                    />
                    <CampaignDetailsCard
                      value={formatPercentageNoMultiply(
                        effectiveMergedCampaignData.metrics.summary.avgVTR
                      )}
                      label={t("campaigns.vtr", { defaultValue: "VTR" })}
                    />
                    <CampaignDetailsCard
                      value={formatCurrency(
                        effectiveMergedCampaignData.metrics.summary.avgCPV,
                        effectiveMergedCampaignData.currency_enum
                      )}
                      label={t("campaigns.cpv", { defaultValue: "CPV" })}
                    />
                  </>
                )}
              </Box>
            )}

            {/* Metrics Timeline Chart */}
            {effectiveMergedCampaignData?.metrics?.dailyData &&
            effectiveMergedCampaignData.metrics.dailyData.length > 0 ? (
              <Box sx={{ mt: 4 }}>
                <MetricsTimelineChart
                  title={t("campaigns.metricsOverTime", {
                    defaultValue: "Metrics Over Time",
                  })}
                  subtitle={t("campaigns.clickMetricsToCompare", {
                    defaultValue:
                      "Click on metrics to compare or select 'All' to see accumulated data",
                  })}
                  data={getChartDataPoints(
                    effectiveMergedCampaignData.metrics.dailyData
                  )}
                  isLoading={effectiveIsLoading}
                />
              </Box>
            ) : (
              <Box
                sx={{
                  mt: 4,
                  p: 3,
                  textAlign: "center",
                  bgcolor: theme.palette.background.default,
                  borderRadius: 2,
                }}
              >
                <Typography variant="body1" color="text.secondary">
                  {t("campaigns.noMetricsData", {
                    defaultValue:
                      "No metrics data available for the selected time period. Data will appear here once the campaign starts generating metrics.",
                  })}
                </Typography>
              </Box>
            )}

            <CreativeMetricsSection
              mergedCampaignId={mergedCampaignId}
              campaignName={mergedCampaignData?.name}
              currency={mergedCampaignData?.currency_enum}
              startDate={formatDateForInput(mergedCampaignData?.startDate)}
              endDate={formatDateForInput(mergedCampaignData?.endDate)}
            />

            <PublisherMetricsSection
              mergedCampaignId={mergedCampaignId}
              campaignName={mergedCampaignData?.name}
              currency={mergedCampaignData?.currency_enum}
              startDate={formatDateForInput(mergedCampaignData?.startDate)}
              endDate={formatDateForInput(mergedCampaignData?.endDate)}
            />
          </Paper>
        </>
      )}
    </>
  );
};

export default NatAdsMergeCampaignDetails;
